import { Typography, Card, CardContent, CardProps, styled } from '@mui/material'
import DisplayValue from 'components/DisplayValue'
import dayjs from 'dayjs'
import { PATIENT_HEALTH_DATA_DETAIL } from 'navigation/CONSTANTS'
import { NavLink } from 'react-router-dom'
import { adjustColorForTheme } from 'styles/utils'
import 'dayjs/locale/pt-br'

dayjs.locale('pt-br')

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none'
}))

const StyledCard = styled(Card)(({ theme }) => ({
  border: 'none',
  borderRadius: '16px',
  boxShadow: `0 2px 8px ${adjustColorForTheme(
    theme.palette.mode,
    theme.palette.defaultComponent.main,
    0.2
  )}`,
  '&:hover': {
    transform: 'scale(1.01)'
  }
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingBottom: `${theme.spacing(3)}!important`,
  height: '130px',
  [theme.breakpoints.up('sm')]: {
    height: '100px'
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative'
}))

const StyledTimestamp = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: '8px',
  textAlign: 'left',
  width: 'unset',
  right: '16px'
}))

/**
 * IHealthCardProps interface that defines the props for the HealthCard component.
 */
export interface IHealthCardProps {
  /** Optional patient ID for linking to health data details */
  patientId?: string
  /** Optional icon to display alongside the values */
  icon: JSX.Element
  /** The title of the health card */
  title: string
  /** The health data string, used for linking */
  data: string
  /** Array of values to display, each with an optional type */
  /** Each value can be a number, string, or null */
  values: Array<{
    type?: string
    value: number | string | null
  }>
  /** Timestamp string paresed for the health data, used for display */
  timestamp: string
  /** Optional flag to hide the card */
  hide?: boolean
  /** Optional style for the card */
  style?: CardProps['style']
}

/**
 * HealthCard component
 * This component renders a card displaying health data for a patient.
 * It includes a title, values, and a timestamp.
 * It uses MUI's Card components and allows for custom styling.
 * @param {IHealthCardProps} props - The properties for the health card component.
 * @returns {JSX.Element} The rendered health card component.
 */
export const HealthCard: React.FC<IHealthCardProps> = props => {
  const { patientId, icon, title, data, values, timestamp, style } = props

  return (
    <StyledNavLink to={`/${PATIENT_HEALTH_DATA_DETAIL(patientId, data)}`}>
      <StyledCard variant='outlined' style={style}>
        <StyledCardContent>
          <Typography variant='body2' fontWeight='bold'>
            {title}
          </Typography>
          {values[0].value !== null && values[0].value !== '' ? (
            <DisplayValue icon={icon} values={values} />
          ) : (
            <Typography variant='body2' color='textSecondary'>
              Sem dados novos
            </Typography>
          )}
          <StyledTimestamp>
            <Typography variant='caption' color='textSecondary'>
              {values[0].value !== null && values[0].value !== ''
                ? dayjs(timestamp).format('DD/MM HH:mm')
                : ''}
            </Typography>
          </StyledTimestamp>
        </StyledCardContent>
      </StyledCard>
    </StyledNavLink>
  )
}

export default HealthCard
